import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Bold, Italic, Heading2, Heading3, Link2, ImagePlus, List, ListOrdered, Minus, Table as TableIcon, Quote, Unlink, Youtube, X, Send, CheckCircle2 } from 'lucide-react';
import { rawFetch, saveProfileContent, publishProfileContent } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Typography } from '@tiptap/extension-typography';
import YoutubeExtension from '@tiptap/extension-youtube';

interface ArtistProfileContentEditorProps {
  slug: string;
  initialContent: string | null;
  publishedContent: string | null;
}

export default function ArtistProfileContentEditor({ slug, initialContent, publishedContent }: ArtistProfileContentEditorProps) {
  const { token } = useAuthStore();
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Link input state for bubble menu
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  // YouTube embed modal state
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const youtubeInputRef = useRef<HTMLInputElement>(null);

  // File input ref for image upload
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Auto-save debounce
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if content differs from published
  const publishedContentRef = useRef(publishedContent);
  publishedContentRef.current = publishedContent;

  // Image upload helper (reuses transmissions/content-image infrastructure)
  const uploadContentImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      const optimized = await optimizeImage(file);

      const presignRes = await rawFetch(`/api/public/tenants/${slug}/transmissions/content-image/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentType: 'image/webp', fileSize: optimized.size }),
      });

      if (!presignRes.ok) throw new Error('Presign failed');
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };

      const uploadRes = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: optimized,
      });

      if (!uploadRes.ok) throw new Error('Upload failed');

      const confirmRes = await rawFetch(`/api/public/tenants/${slug}/transmissions/content-image/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: presignJson.data.path }),
      });

      if (!confirmRes.ok) throw new Error('Confirm failed');
      const confirmJson = await confirmRes.json() as { data: { url: string } };
      return confirmJson.data.url;
    } catch (err) {
      console.error('Content image upload failed:', err);
      return null;
    }
  }, [slug, token]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        blockquote: {},
        horizontalRule: {},
        bulletList: {},
        orderedList: {},
      }),
      Placeholder.configure({
        placeholder: 'Write your artist page content here...',
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-electric-cyan underline underline-offset-2 hover:text-electric-cyan/80 transition-colors cursor-pointer',
        },
      }),
      ImageResize.configure({
        inline: true,
        minWidth: 100,
        maxWidth: 800,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-white/10 w-full',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border border-white/10',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 text-gray-300',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-white/10 p-2 bg-white/5 text-white font-semibold',
        },
      }),
      Typography,
      YoutubeExtension.configure({
        nocookie: true,
        controls: true,
        allowFullscreen: true,
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'rounded-lg overflow-hidden my-4',
        },
      }),
    ],
    content: (() => {
      if (!initialContent) return null;
      if (typeof initialContent === 'object') return initialContent;
      try {
        return JSON.parse(initialContent);
      } catch {
        return null;
      }
    })(),
    onUpdate: ({ editor }) => {
      scheduleAutoSave(editor.getJSON());
      setHasUnsavedChanges(true);
      setIsPublished(false);
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[400px] prose prose-invert max-w-none prose-headings:text-white prose-p:text-gray-300 prose-a:text-electric-cyan prose-strong:text-white prose-img:rounded-lg prose-img:max-w-full prose-blockquote:border-electric-cyan/40 prose-blockquote:text-gray-400 prose-hr:border-white/10',
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;

        event.preventDefault();
        imageFiles.forEach(async (file) => {
          const url = await uploadContentImage(file);
          if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
        if (!imageItem) return false;

        const file = imageItem.getAsFile();
        if (!file) return false;

        event.preventDefault();
        (async () => {
          const url = await uploadContentImage(file);
          if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        })();
        return true;
      },
    },
  });

  // Auto-save logic (2s debounce)
  const scheduleAutoSave = useCallback((json: any) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving');
      try {
        const content = JSON.stringify(json);
        const res = await saveProfileContent(slug, content, token);
        if (res.ok) {
          setAutoSaveStatus('saved');
          setHasUnsavedChanges(false);
          setTimeout(() => setAutoSaveStatus('idle'), 2000);
        } else {
          setAutoSaveStatus('idle');
        }
      } catch {
        setAutoSaveStatus('idle');
      }
    }, 2000);
  }, [slug, token]);

  // Publish content
  const handlePublish = useCallback(async () => {
    if (!editor) return;

    setPublishing(true);
    try {
      // First ensure the latest content is saved
      const content = JSON.stringify(editor.getJSON());
      await saveProfileContent(slug, content, token);

      // Then publish
      const res = await publishProfileContent(slug, token);
      if (res.ok) {
        setIsPublished(true);
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Publish failed:', err);
    } finally {
      setPublishing(false);
    }
  }, [editor, slug, token]);

  // Cleanup auto-save timer
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Image optimize helper
  const optimizeImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          resolve(blob || file);
        }, 'image/webp', 0.82);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Image select handler
  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    e.target.value = '';

    const url = await uploadContentImage(file);
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, uploadContentImage]);

  // Link helpers
  const openLinkInput = useCallback(() => {
    if (editor?.isActive('link')) {
      setLinkUrl(editor.getAttributes('link').href || '');
    }
    setShowLinkInput(true);
    setTimeout(() => linkInputRef.current?.focus(), 100);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor || !linkUrl.trim()) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run();
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor]);

  // YouTube helpers
  const openYoutubeModal = useCallback(() => {
    setShowYoutubeModal(true);
    setTimeout(() => youtubeInputRef.current?.focus(), 100);
  }, []);

  const embedYoutube = useCallback(() => {
    if (!editor || !youtubeUrl.trim()) return;
    editor.commands.setYoutubeVideo({ src: youtubeUrl.trim() });
    setShowYoutubeModal(false);
    setYoutubeUrl('');
  }, [editor, youtubeUrl]);

  return (
    <div className="w-full">
      {/* Header with status and publish button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-mono uppercase tracking-wider text-text-secondary">Profile Content</h3>
          {autoSaveStatus === 'saving' && (
            <span className="flex items-center gap-1.5 text-xs text-text-muted font-mono">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          {autoSaveStatus === 'saved' && (
            <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono">
              <CheckCircle2 className="w-3 h-3" />
              Saved
            </span>
          )}
        </div>
        <button
          onClick={handlePublish}
          disabled={publishing || isPublished}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-mono font-semibold bg-electric-cyan text-black hover:bg-electric-cyan/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {publishing ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Publishing...
            </>
          ) : isPublished ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Published
            </>
          ) : (
            <>
              <Send className="w-3.5 h-3.5" />
              Publish Content
            </>
          )}
        </button>
      </div>

      {/* Editor */}
      <div className="glass-card p-4 relative">
        {/* Bubble Menu - appears over selected text */}
        {editor && (
          <BubbleMenu
            editor={editor}
            options={{ placement: 'top', offset: 8 }}
            className="flex items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-lg p-1 shadow-xl"
          >
            {showLinkInput ? (
              <div className="flex items-center gap-1 px-1">
                <input
                  ref={linkInputRef}
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                    if (e.key === 'Escape') {
                      setShowLinkInput(false);
                      setLinkUrl('');
                    }
                  }}
                  placeholder="https://..."
                  className="w-48 px-2 py-1 text-xs bg-white/5 border border-white/10 rounded text-white placeholder:text-white/30 outline-none focus:border-electric-cyan/50"
                />
                <button
                  onClick={applyLink}
                  className="p-1.5 rounded text-electric-cyan hover:bg-electric-cyan/10 transition-colors"
                  title="Apply link"
                >
                  <Link2 className="w-3.5 h-3.5" />
                </button>
                {editor.isActive('link') && (
                  <button
                    onClick={removeLink}
                    className="p-1.5 rounded text-red-400 hover:bg-red-400/10 transition-colors"
                    title="Remove link"
                  >
                    <Unlink className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}
                  className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('bold') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('italic') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <div className="w-px h-4 bg-white/10 mx-0.5" />
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Heading 2"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('heading', { level: 3 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Heading 3"
                >
                  <Heading3 className="w-4 h-4" />
                </button>
                <div className="w-px h-4 bg-white/10 mx-0.5" />
                <button
                  onClick={openLinkInput}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('link') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Link"
                >
                  <Link2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={`p-1.5 rounded transition-colors ${editor.isActive('blockquote') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  title="Blockquote"
                >
                  <Quote className="w-4 h-4" />
                </button>
              </>
            )}
          </BubbleMenu>
        )}

        {/* Floating Menu - appears on empty lines */}
        {editor && (
          <FloatingMenu
            editor={editor}
            options={{ placement: 'top', offset: 8 }}
            className="flex items-center gap-1"
          >
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Heading 2"
            >
              <Heading2 className="w-3.5 h-3.5" />
              <span>H2</span>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Bullet List"
            >
              <List className="w-3.5 h-3.5" />
              <span>List</span>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Ordered List"
            >
              <ListOrdered className="w-3.5 h-3.5" />
              <span>1. 2. 3.</span>
            </button>
            <button
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Horizontal Rule"
            >
              <Minus className="w-3.5 h-3.5" />
              <span>Divider</span>
            </button>
            <button
              onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Insert Table (3×3)"
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span>Table</span>
            </button>
            <button
              onClick={() => imageInputRef.current?.click()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Upload image"
            >
              <ImagePlus className="w-3.5 h-3.5" />
              <span>Image</span>
            </button>
            <button
              onClick={openYoutubeModal}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-electric-cyan hover:border-electric-cyan/30 transition-colors text-xs font-mono"
              title="Embed YouTube video"
            >
              <Youtube className="w-3.5 h-3.5" />
              <span>YouTube</span>
            </button>
          </FloatingMenu>
        )}

        <EditorContent editor={editor} />

        {/* Hidden file input for image uploads */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleImageSelect}
        />
      </div>

      {/* YouTube Embed Modal */}
      {showYoutubeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-500" />
                Embed YouTube Video
              </h3>
              <button
                onClick={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }}
                className="p-1 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              ref={youtubeInputRef}
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  embedYoutube();
                }
                if (e.key === 'Escape') {
                  setShowYoutubeModal(false);
                  setYoutubeUrl('');
                }
              }}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 outline-none focus:border-electric-cyan/50 transition-colors"
            />
            <p className="text-xs text-text-muted mt-2">Paste any YouTube or YouTube Shorts URL</p>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }}
                className="px-3 py-1.5 rounded-lg text-xs font-mono text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={embedYoutube}
                disabled={!youtubeUrl.trim()}
                className="px-4 py-1.5 rounded-lg text-xs font-mono bg-electric-cyan text-black font-semibold hover:bg-electric-cyan/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Embed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
